/**
 * ============================================================================
 *  SITE CONFIGURATION — single source of truth for the whole website.
 * ============================================================================
 *  Edit the values below to update the site. Everything (header, footer,
 *  home, about, services, contact, legal pages and SEO) reads from here.
 *  No database, no admin panel — just change this file and rebuild.
 * ----------------------------------------------------------------------------
 */

export const siteInfo = {
  // ---- Brand --------------------------------------------------------------
  companyName: 'Easy Moove Ltd',
  brandName: 'Easy Moove',
  tagline: 'Investing in Property. Building Opportunities.',
  locationLabel: 'Preston, United Kingdom',
  foundedYear: 2017,

  // Short line used in the hero / meta description
  shortDescription:
    'A Preston-based property investment company focused on acquiring, developing and managing property investments for the long term.',

  // Longer paragraph used on the home/about pages
  longDescription:
    'Easy Moove Ltd is a UK-based property investment company focused on acquiring, developing and managing property investments for the long term. We invest in property through buying, selling and letting opportunities, building a diverse portfolio designed to create long-term value. Our approach is focused on identifying opportunities, making informed investment decisions and managing properties effectively.',

  // Supporting investors paragraph
  investorSupport:
    'We also work with other property investors, helping them identify opportunities to buy and sell property and supporting them throughout the investment process. Whether you are looking to build your own property portfolio, sell an existing property, or explore new investment opportunities, we aim to provide practical support and a straightforward approach.',

  // Aliases used by page copy
  aboutExtra:
    'We also work with other property investors, helping them identify opportunities to buy and sell property and supporting them throughout the investment process. Whether you are looking to build your own property portfolio, sell an existing property, or explore new investment opportunities, we aim to provide practical support and a straightforward approach.',

  approachStatement:
    'Whether you are looking to build your own property portfolio, sell an existing property, or explore new investment opportunities, we aim to provide practical support and a straightforward approach.',

  // Belief / closing statement
  belief:
    'At Easy Moove Ltd, we believe property investment is built on identifying the right opportunities, making informed decisions and creating long-term value.',

  closingLine: 'Property. Investment. Opportunities.',

  // Five focus tiles under the homepage hero (client mockup)
  heroFocus: [
    {
      title: 'Property Investment',
      description: 'Building a strong portfolio through strategic property investments.'
    },
    {
      title: 'Buying & Selling',
      description: 'Identifying opportunities and delivering smooth property transactions.'
    },
    {
      title: 'Rental & Management',
      description: 'Letting and managing properties to maximise returns and value.'
    },
    {
      title: 'Investor Support',
      description: 'Helping investors buy and sell properties with confidence.'
    },
    {
      title: 'Long-Term Value',
      description: 'Focused on sustainable growth and long-term opportunities.'
    }
  ],

  // ---- Contact details ----------------------------------------------------
  contact: {
    phone: '',
    email: 'info@easymooveltd.co.uk',
    addressLine1: '',
    city: 'Preston',
    postcode: '',
    country: 'United Kingdom',
    // Full single-line address (shown across the site)
    fullAddress: 'Preston, United Kingdom'
  },

  // ---- Opening hours ------------------------------------------------------
  openingHours: [
    { day: 'Monday – Friday', hours: '9:00am – 5:00pm' },
    { day: 'Saturday – Sunday', hours: 'Closed' }
  ],

  // ---- Social links (set to '' to hide) -----------------------------------
  social: {
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: ''
  },

  // ---- Mission & values (About page) --------------------------------------
  mission:
    'To identify the right property opportunities, make informed investment decisions, and create long-term value through careful acquisition, development and management.',

  vision:
    'To build a trusted property investment business — based in Preston and working across the UK — recognised for a straightforward approach and lasting partnerships.',

  values: [
    {
      title: 'Opportunity First',
      description: 'We focus on identifying the right opportunities before we invest.'
    },
    {
      title: 'Informed Decisions',
      description: 'Every decision is grounded in clear information and practical judgement.'
    },
    {
      title: 'Long-Term Value',
      description: 'We build and manage portfolios designed for lasting returns, not quick wins.'
    },
    {
      title: 'Straightforward Support',
      description: 'We support other investors with clear, practical help throughout the process.'
    }
  ],

  // ---- Services / Our Focus -----------------------------------------------
  services: [
    {
      icon: '',
      title: 'Property Investment & Portfolio Building',
      description:
        'We acquire, develop and manage property investments with a focus on building a diverse portfolio for long-term value.'
    },
    {
      icon: '',
      title: 'Buying & Selling Property',
      description:
        'We buy and sell property as part of our investment activity, and help other investors do the same with practical support.'
    },
    {
      icon: '',
      title: 'Residential & Commercial Opportunities',
      description:
        'We look across residential and commercial property to identify opportunities that fit a long-term investment approach.'
    },
    {
      icon: '',
      title: 'Letting & Rental Investments',
      description:
        'We invest through letting and rental opportunities, managing properties effectively to support lasting returns.'
    },
    {
      icon: '',
      title: 'Support for Other Investors',
      description:
        'We work with other property investors to identify buy and sell opportunities and support them through the investment process.'
    },
    {
      icon: '',
      title: 'Long-Term Opportunity Identification',
      description:
        'Our focus is finding opportunities that create sustainable value over time — not short-term speculation.'
    }
  ],

  // ---- Focus list (Home / About bullets) ----------------------------------
  focusAreas: [
    'Property investment and portfolio building',
    'Buying and selling property',
    'Residential and commercial property opportunities',
    'Property letting and rental investments',
    'Supporting other investors with property transactions',
    'Identifying opportunities for long-term value'
  ],

  // ---- Headline stats (kept minimal / factual) ----------------------------
  stats: [
    { value: 'Preston', label: 'Based in' },
    { value: 'UK Wide', label: 'Opportunities' },
    { value: 'Long-Term', label: 'Investment Focus' },
    { value: 'Practical', label: 'Investor Support' }
  ],

  // ---- Legal / company info (footer + legal pages) ------------------------
  legal: {
    companyType: 'Private Limited Company',
    companyNumber: '10690349',
    vatNumber: '',
    tpoSchemeName: '',
    tpoMembership: '',
    privacyEmail: 'info@easymooveltd.co.uk',
    complaintsEmail: 'info@easymooveltd.co.uk',
    legalUpdated: '23 July 2026'
  },

  // ---- SEO defaults -------------------------------------------------------
  seo: {
    defaultDescription:
      'Easy Moove Ltd — Preston-based property investment company focused on acquiring, developing and managing property investments for the long term.',
    ogImage: 'https://picsum.photos/seed/easymoove/1200/630'
  }
};

/** Build a `tel:` href from a display phone number. */
export function phoneToTelHref(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits ? `tel:${digits}` : '';
}

/** Build a Google Maps search link for the business address. */
export function mapsHref(address = siteInfo.contact.fullAddress) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

export default siteInfo;
